/**
 * 문의사항 API (어드민)
 *
 * - GET    /api/admin/inquiries
 * - GET    /api/admin/inquiries/pending-count
 * - GET    /api/admin/inquiries/{id}
 * - PUT    /api/admin/inquiries/{id}/status
 * - DELETE /api/admin/inquiries/{id}
 */
import { apiClient } from './client';
import type { BadgeTone } from '@/components/common';

export type InquiryCategory = 'STORE_INFO' | 'BIZ_IMPROVE' | 'ETC';
export type InquirySourceApp = 'GUARDIAN' | 'BUSINESS';
export type InquiryStatus = 'RECEIVED' | 'IN_PROGRESS' | 'DONE' | 'REJECTED';

export const INQUIRY_CATEGORY_LABEL: Record<InquiryCategory, string> = {
  STORE_INFO: '매장정보 제안',
  BIZ_IMPROVE: '개선사항',
  ETC: '기타',
};

export const INQUIRY_SOURCE_LABEL: Record<InquirySourceApp, string> = {
  GUARDIAN: '보호자앱',
  BUSINESS: '사업주앱',
};

export const INQUIRY_STATUS_LABEL: Record<InquiryStatus, string> = {
  RECEIVED: '접수',
  IN_PROGRESS: '처리중',
  DONE: '완료',
  REJECTED: '반려',
};

// makeBadgeCell 의 BadgeTone: 'neutral'|'success'|'danger'|'warning'|'info'|'primary'
export const INQUIRY_STATUS_TONE: Record<InquiryStatus, BadgeTone> = {
  RECEIVED: 'warning',
  IN_PROGRESS: 'info',
  DONE: 'success',
  REJECTED: 'neutral',
};

export interface Inquiry {
  id: number;
  category: InquiryCategory;
  categoryLabel?: string | null;
  sourceApp: InquirySourceApp;
  sourceAppLabel?: string | null;
  subType?: string | null;
  companySeq?: number | null;
  companyName?: string | null;
  content: string;
  contact?: string | null;
  writerUserSeq?: number | null;
  status: InquiryStatus;
  statusLabel?: string | null;
  adminMemo?: string | null;
  handlerUserSeq?: number | null;
  answer?: string | null;
  answeredAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface InquiryStatusUpdateInput {
  status: InquiryStatus;
  adminMemo?: string | null;
  answer?: string | null;
}

export interface GetInquiriesParams {
  page?: number;
  size?: number;
  category?: InquiryCategory | 'ALL';
  status?: InquiryStatus | 'ALL';
}

export interface InquiryPage {
  content: Inquiry[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

const BASE = '/admin/inquiries';

export const inquiriesApi = {
  getInquiries: async (
    params: GetInquiriesParams = {}
  ): Promise<InquiryPage> => {
    const res = await apiClient.get<InquiryPage>(BASE, {
      params: {
        page: params.page ?? 0,
        size: params.size ?? 10,
        category:
          params.category && params.category !== 'ALL'
            ? params.category
            : undefined,
        status:
          params.status && params.status !== 'ALL' ? params.status : undefined,
      },
    });
    return res.data;
  },

  getPendingCount: async (): Promise<number> => {
    const res = await apiClient.get<number>(`${BASE}/pending-count`);
    return res.data;
  },

  getInquiryDetail: async (id: number): Promise<Inquiry> => {
    const res = await apiClient.get<Inquiry>(`${BASE}/${id}`);
    return res.data;
  },

  updateStatus: async (
    id: number,
    input: InquiryStatusUpdateInput
  ): Promise<Inquiry> => {
    const res = await apiClient.put<Inquiry>(`${BASE}/${id}/status`, input);
    return res.data;
  },

  deleteInquiry: async (id: number): Promise<void> => {
    await apiClient.delete<void>(`${BASE}/${id}`);
  },
};
